/**
 * WAX Treasury Service
 *
 * Service for monitoring WAX treasury account balance and resources.
 * Used to ensure the platform has sufficient resources for account creation and minting.
 * Uses Wharfkit for blockchain queries.
 * Migrated from eosjs to address CVE-2024-968p security vulnerability.
 */

import { getWaxConfig, isTreasuryConfigured } from './config';
import { getWharfkitRpcClient } from './wharfkit-server';
import { logger } from '../logger';

// Default thresholds (can be overridden via environment variables)
const DEFAULT_WAX_MIN_BALANCE = 100; // WAX tokens
const DEFAULT_RAM_MIN_BYTES = 102400; // 100KB

// Cost per WAX account creation (in WAX)
const WAX_PER_ACCOUNT_CREATION = 5;
// Buffer to keep in treasury (in WAX)
const TREASURY_BUFFER_WAX = 10;

export interface TreasuryBalance {
  account: string;
  balance: string;
  balanceFloat: number;
  currency: string;
  network: string;
}

export interface TreasuryResources {
  waxBalance: number;
  ramBytes: number;
  ramUsed: number;
  ramAvailable: number;
  timestamp: Date;
}

export interface ThresholdCheck {
  waxLow: boolean;
  ramLow: boolean;
  alerts: string[];
  resources?: TreasuryResources;
}

/**
 * Get the minimum WAX balance threshold from environment or default
 */
export function getMinWaxBalance(): number {
  const envValue = process.env.WAX_MIN_BALANCE;
  if (envValue) {
    const parsed = parseFloat(envValue);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  return DEFAULT_WAX_MIN_BALANCE;
}

/**
 * Get the minimum RAM bytes threshold from environment or default
 */
export function getMinRamBytes(): number {
  const envValue = process.env.RAM_MIN_BYTES;
  if (envValue) {
    const parsed = parseInt(envValue, 10);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  return DEFAULT_RAM_MIN_BYTES;
}

/**
 * Parse WAX balance string to number
 * Format: "123.45678901 WAX"
 */
function parseWaxBalance(balanceStr: string | undefined): number {
  if (!balanceStr) return 0;
  const match = balanceStr.match(/^([\d.]+)\s+WAX$/);
  if (!match) return 0;
  return parseFloat(match[1]);
}

export class WaxTreasury {
  private config = getWaxConfig();

  /**
   * Check if the service is properly configured
   */
  isConfigured(): boolean {
    return isTreasuryConfigured() && !!this.config.treasuryAccount;
  }

  /**
   * Get current treasury balance
   */
  async getBalance(): Promise<TreasuryBalance | null> {
    try {
      if (!this.config.treasuryAccount) {
        throw new Error('Treasury account not configured');
      }

      const rpcClient = getWharfkitRpcClient();
      const result = await rpcClient.getCurrencyBalance(
        this.config.treasuryAccount,
        'eosio.token',
        'WAX'
      );

      if (!result.success || !result.data || result.data.length === 0) {
        return {
          account: this.config.treasuryAccount,
          balance: '0.00000000 WAX',
          balanceFloat: 0,
          currency: 'WAX',
          network: this.config.network
        };
      }

      const balance = result.data[0];
      const balanceFloat = parseFloat(balance.split(' ')[0]);

      return {
        account: this.config.treasuryAccount,
        balance,
        balanceFloat,
        currency: 'WAX',
        network: this.config.network
      };

    } catch (error) {
      logger.error("Error getting treasury balance", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return null;
    }
  }

  /**
   * Get treasury resources including WAX balance and RAM
   */
  async getTreasuryResources(): Promise<TreasuryResources> {
    if (!this.isConfigured()) {
      logger.warn("Treasury account not configured");
      return {
        waxBalance: 0,
        ramBytes: 0,
        ramUsed: 0,
        ramAvailable: 0,
        timestamp: new Date(),
      };
    }

    try {
      const rpcClient = getWharfkitRpcClient();
      // treasuryAccount is guaranteed to be defined after isConfigured() check
      const accountResult = await rpcClient.getAccount(this.config.treasuryAccount!);

      if (!accountResult.success || !accountResult.data) {
        throw new Error(accountResult.error || 'Failed to get account info');
      }

      const accountInfo = accountResult.data;
      const waxBalance = parseWaxBalance(accountInfo.core_liquid_balance?.toString());
      const ramBytes = accountInfo.ram_quota || 0;
      const ramUsed = accountInfo.ram_usage || 0;
      const ramAvailable = ramBytes - ramUsed;

      logger.info("Treasury resources retrieved", {
        waxBalance,
        ramBytes,
        ramUsed
      });

      return {
        waxBalance,
        ramBytes,
        ramUsed,
        ramAvailable,
        timestamp: new Date(),
      };
    } catch (error) {
      logger.error("Failed to get treasury resources", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      throw error;
    }
  }

  /**
   * Check if treasury resources are below configured thresholds
   */
  async checkResourceThresholds(): Promise<ThresholdCheck> {
    const minWaxBalance = getMinWaxBalance();
    const minRamBytes = getMinRamBytes();

    const alerts: string[] = [];
    let waxLow = false;
    let ramLow = false;
    let resources: TreasuryResources | undefined;

    try {
      resources = await this.getTreasuryResources();

      // Check WAX balance threshold
      if (resources.waxBalance < minWaxBalance) {
        waxLow = true;
        const alert = `ALERT: WAX balance (${resources.waxBalance.toFixed(2)} WAX) is below minimum threshold (${minWaxBalance} WAX)`;
        alerts.push(alert);
        logger.warn("WAX balance below threshold", {
          currentBalance: resources.waxBalance,
          minBalance: minWaxBalance
        });
      }

      // Check RAM threshold
      if (resources.ramAvailable < minRamBytes) {
        ramLow = true;
        const alert = `ALERT: RAM available (${resources.ramAvailable} bytes) is below minimum threshold (${minRamBytes} bytes)`;
        alerts.push(alert);
        logger.warn("RAM available below threshold", {
          ramAvailable: resources.ramAvailable,
          minRamBytes
        });
      }

      return {
        waxLow,
        ramLow,
        alerts,
        resources,
      };
    } catch (error) {
      const errorAlert = `ERROR: Failed to check treasury resources: ${error instanceof Error ? error.message : 'Unknown error'}`;
      alerts.push(errorAlert);
      logger.error("Failed to check treasury resources", {
        error: error instanceof Error ? error.message : "Unknown error"
      });

      return {
        waxLow: false, // Unknown state, don't block operations
        ramLow: false,
        alerts,
      };
    }
  }

  /**
   * Check if treasury has sufficient resources for minting
   */
  async canMint(): Promise<{ allowed: boolean; reason?: string }> {
    const thresholds = await this.checkResourceThresholds();

    if (thresholds.waxLow) {
      return {
        allowed: false,
        reason: `Treasury WAX balance too low. Please contact support.`,
      };
    }

    if (thresholds.ramLow) {
      return {
        allowed: false,
        reason: `Treasury RAM insufficient. Please contact support.`,
      };
    }

    return { allowed: true };
  }

  /**
   * Check if treasury has sufficient funds for account creation
   */
  async hasSufficientFunds(accountsToCreate: number = 1): Promise<boolean> {
    try {
      const resources = await this.getTreasuryResources();
      const requiredWax = accountsToCreate * WAX_PER_ACCOUNT_CREATION;
      const totalRequired = requiredWax + TREASURY_BUFFER_WAX;

      const hasFunds = resources.waxBalance >= totalRequired;

      if (!hasFunds) {
        logger.warn("Insufficient treasury funds for account creation", {
          waxBalance: resources.waxBalance,
          accountsToCreate,
          requiredWax: totalRequired
        });
      }

      return hasFunds;
    } catch (error) {
      logger.error("Error checking treasury funds", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return false;
    }
  }

  /**
   * Get treasury account info
   */
  async getAccountInfo() {
    try {
      if (!this.config.treasuryAccount) {
        throw new Error('Treasury account not configured');
      }

      const rpcClient = getWharfkitRpcClient();
      const result = await rpcClient.getAccount(this.config.treasuryAccount);

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to get account info');
      }

      const accountInfo = result.data;

      return {
        account: this.config.treasuryAccount,
        created: accountInfo.created,
        ramQuota: accountInfo.ram_quota,
        ramUsage: accountInfo.ram_usage,
        cpuLimit: accountInfo.cpu_limit,
        netLimit: accountInfo.net_limit,
        network: this.config.network
      };

    } catch (error) {
      logger.error("Error getting treasury account info", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return null;
    }
  }
}

export const waxTreasury = new WaxTreasury();
